import React from "react";
import { Link } from "react-router-dom";
import { motion, useInView, useMotionValue, animate, AnimatePresence } from "framer-motion";
import {
    ArrowRight,
    Compass,
    GitMerge,
    Rocket,
    ChevronRight,
    Sparkles,
    Shield,
    LineChart,
    Users2,
} from "lucide-react";
import { Button } from "../components/ui/button";
import FrameworkWheel, { DOMAINS } from "../components/FrameworkWheel";
import { PublicNav, PublicFooter } from "../components/PublicChrome";

const MORPH_PHRASES = [
    "organizational excellence.",
    "coherent leadership.",
    "measurable student outcomes.",
];

function MorphHeadline() {
    const [idx, setIdx] = React.useState(0);
    React.useEffect(() => {
        if (idx >= MORPH_PHRASES.length - 1) return; // settle on phrase 3
        const t = setTimeout(() => setIdx((i) => i + 1), 1800);
        return () => clearTimeout(t);
    }, [idx]);
    return (
        <h1
            className="font-serif font-semibold text-white leading-[1.05] tracking-tight"
            style={{ fontSize: "clamp(2.5rem, 5vw, 4.5rem)" }}
            data-testid="hero-headline"
        >
            One integrated methodology for
            <br />
            <span className="relative inline-block min-h-[1.2em] w-full">
                <AnimatePresence mode="wait">
                    <motion.span
                        key={idx}
                        initial={{ opacity: 0, y: 12, filter: "blur(8px)" }}
                        animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                        exit={{ opacity: 0, y: -12, filter: "blur(8px)" }}
                        transition={{ duration: 0.6, ease: "easeOut" }}
                        className="inline-block bg-clip-text text-transparent"
                        style={{
                            backgroundImage: "linear-gradient(120deg, #FFFFFF 0%, #8AC4FF 60%, #D6E6F2 100%)",
                        }}
                        data-testid={`hero-morph-phrase-${idx}`}
                    >
                        {MORPH_PHRASES[idx]}
                    </motion.span>
                </AnimatePresence>
            </span>
        </h1>
    );
}

function CountUp({ to, suffix = "", duration = 2 }) {
    const ref = React.useRef(null);
    const inView = useInView(ref, { once: true, margin: "-40px" });
    const mv = useMotionValue(0);
    const [display, setDisplay] = React.useState(0);
    React.useEffect(() => {
        if (!inView) return;
        const controls = animate(mv, to, { duration, ease: "easeOut" });
        const unsub = mv.on("change", (v) => setDisplay(v));
        return () => {
            controls.stop();
            unsub();
        };
    }, [inView, to]);
    return (
        <span ref={ref}>
            {Number.isInteger(to) ? Math.floor(display) : display.toFixed(1)}
            {suffix}
        </span>
    );
}

function OhiRing({ target = 78, size = 56 }) {
    const ref = React.useRef(null);
    const inView = useInView(ref, { once: true });
    const [value, setValue] = React.useState(0);
    React.useEffect(() => {
        if (!inView) return;
        const mv = { v: 0 };
        const controls = animate(0, target, {
            duration: 1.6,
            ease: "easeOut",
            onUpdate: (v) => setValue(v),
        });
        return () => controls.stop();
    }, [inView, target]);
    const circumference = 2 * Math.PI * 22;
    const offset = circumference - (value / 100) * circumference;
    return (
        <div ref={ref} className="relative" style={{ width: size, height: size }}>
            <svg viewBox="0 0 56 56" className="w-full h-full -rotate-90">
                <circle cx="28" cy="28" r="22" stroke="#D6E6F2" strokeWidth="5" fill="none" />
                <circle
                    cx="28"
                    cy="28"
                    r="22"
                    stroke="#1E56A0"
                    strokeWidth="5"
                    fill="none"
                    strokeLinecap="round"
                    strokeDasharray={circumference}
                    strokeDashoffset={offset}
                />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center font-serif font-semibold text-[#0B2545]" style={{ fontSize: 14 }}>
                {Math.round(value)}
            </div>
        </div>
    );
}

const HERO_STATS = [
    { value: 6, label: "Domains of Coherence", suffix: "" },
    { value: 216, label: "Reflection Prompts", suffix: "" },
    { value: 4, label: "Level Rubric Rigor", suffix: "-Level", integer: true },
    { value: 100, label: "Audit-Defensible", suffix: "%" },
];

function StatsStrip() {
    return (
        <section className="bg-[#0B2545] text-white py-16 px-6 lg:px-10 border-y border-white/5" data-testid="stats-strip">
            <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6 lg:gap-10">
                {HERO_STATS.map((s, i) => (
                    <motion.div
                        key={s.label}
                        initial={{ opacity: 0, y: 12 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-40px" }}
                        transition={{ duration: 0.5, delay: i * 0.08 }}
                        className="text-center md:text-left"
                        data-testid={`stat-tile-${i}`}
                    >
                        <div className="font-serif font-semibold leading-none" style={{ fontSize: "clamp(2.5rem, 5vw, 4rem)", color: "#8AC4FF" }}>
                            <CountUp to={s.value} suffix={s.suffix} />
                        </div>
                        <div className="mt-3 text-xs uppercase tracking-[0.25em] text-white/70 font-semibold">
                            {s.label}
                        </div>
                    </motion.div>
                ))}
            </div>
        </section>
    );
}

function DomainPreviewGrid() {
    const [active, setActive] = React.useState(null);
    return (
        <section className="bg-white text-[#0E1B2C] py-24 px-6 lg:px-10" data-testid="domain-preview-section">
            <div className="max-w-7xl mx-auto">
                <div className="max-w-2xl mb-8">
                    <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#1E56A0] mb-3">
                        Click a domain to explore
                    </div>
                    <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#0B2545] font-semibold leading-tight">
                        The six domains, at a glance.
                    </h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5" data-testid="domain-preview-grid">
                    {DOMAINS.map((d) => {
                        const isActive = active === d.id;
                        return (
                            <button
                                key={d.id}
                                onClick={() => setActive(isActive ? null : d.id)}
                                onMouseEnter={() => setActive(d.id)}
                                onMouseLeave={() => setActive((cur) => (cur === d.id ? null : cur))}
                                className="group relative rounded-2xl p-6 text-white text-left overflow-hidden min-h-[220px] shadow-lg shadow-[#0B2545]/10 hover:shadow-xl hover:shadow-[#0B2545]/20 transition-all"
                                style={{ backgroundColor: d.color }}
                                data-testid={`domain-preview-${d.id}`}
                            >
                                <AnimatePresence mode="wait">
                                    {isActive ? (
                                        <motion.div
                                            key="back"
                                            initial={{ opacity: 0, rotateY: -90 }}
                                            animate={{ opacity: 1, rotateY: 0 }}
                                            exit={{ opacity: 0, rotateY: 90 }}
                                            transition={{ duration: 0.35 }}
                                            className="text-sm leading-relaxed"
                                            data-testid={`domain-preview-def-${d.id}`}
                                        >
                                            <div className="font-serif font-semibold text-base mb-2">{d.name}</div>
                                            <div className="text-white/90">{d.definition}</div>
                                        </motion.div>
                                    ) : (
                                        <motion.div
                                            key="front"
                                            initial={{ opacity: 0, rotateY: 90 }}
                                            animate={{ opacity: 1, rotateY: 0 }}
                                            exit={{ opacity: 0, rotateY: -90 }}
                                            transition={{ duration: 0.35 }}
                                            className="h-full flex flex-col justify-between"
                                        >
                                            <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-white/15 backdrop-blur border border-white/25">
                                                <d.Icon size={22} />
                                            </div>
                                            <div>
                                                <div className="font-serif text-xl font-semibold leading-tight">{d.name}</div>
                                                <div className="mt-2 text-xs uppercase tracking-[0.25em] text-white/80">Hover / tap to reveal</div>
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </button>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

function SplitBanner() {
    return (
        <section className="bg-[#D6E6F2] py-24 px-6 lg:px-10" data-testid="split-banner">
            <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                <motion.div
                    initial={{ opacity: 0, x: -16 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.6 }}
                    className="relative rounded-3xl overflow-hidden shadow-2xl shadow-[#0B2545]/20"
                >
                    <img src="/widget-students.jpg" alt="Students engaged in collaborative learning" className="w-full h-full object-cover" style={{ minHeight: 320 }} />
                    <div className="absolute top-4 left-4 rounded-full bg-white/95 text-[#0B2545] text-xs uppercase tracking-[0.25em] font-bold px-3 py-1.5">
                        Reviewer Perspective
                    </div>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, x: 16 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.6, delay: 0.1 }}
                >
                    <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#1E56A0] mb-3">See it in action</div>
                    <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#0B2545] font-semibold leading-tight">
                        From classroom evidence to executive dashboard in one system.
                    </h2>
                    <ul className="mt-8 space-y-3 text-[#0E1B2C]/85">
                        {[
                            "Score 4-level rubrics across 6 domains",
                            "Upload evidence, findings, and red flags",
                            "Generate board-ready PDF reports",
                            "Track OHI weighted score across cycles",
                        ].map((b) => (
                            <li key={b} className="flex items-start gap-3">
                                <span className="mt-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#0B2545] text-white flex-shrink-0">
                                    <ChevronRight size={12} />
                                </span>
                                <span>{b}</span>
                            </li>
                        ))}
                    </ul>
                </motion.div>
            </div>
        </section>
    );
}

function ValueProps() {
    const items = [
        { Icon: Compass, title: "Diagnose", body: "Uncover coherence gaps across all six domains with rubric-driven, evidence-backed assessments." },
        { Icon: GitMerge, title: "Align", body: "Translate vision into cross-functional priorities that every campus, coach and teacher can act on." },
        { Icon: Rocket, title: "Execute", body: "Measure, monitor and sustain progress with real-time dashboards, evidence trails and audit-grade export." },
    ];
    return (
        <section id="pillars" className="bg-white text-[#0E1B2C] py-24 px-6 lg:px-10" data-testid="value-props">
            <div className="max-w-7xl mx-auto">
                <div className="max-w-2xl mb-14">
                    <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#1E56A0] mb-3">Three moves. One system.</div>
                    <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#0B2545] font-semibold leading-tight">Diagnose · Align · Execute</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
                    {items.map(({ Icon, title, body }, idx) => (
                        <motion.div
                            key={title}
                            initial={{ opacity: 0, y: 18 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-80px" }}
                            transition={{ duration: 0.5, delay: idx * 0.08 }}
                            className="group rounded-2xl border border-[#D6E6F2] bg-white p-8 hover:border-[#8AC4FF] hover:shadow-xl hover:shadow-[#0B2545]/5 transition-all"
                            data-testid={`value-prop-${title.toLowerCase()}`}
                        >
                            <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#0B2545] text-white mb-6 group-hover:bg-[#1E56A0] transition-colors">
                                <Icon size={22} />
                            </div>
                            <h3 className="font-serif text-2xl text-[#0B2545] font-semibold mb-2">{title}</h3>
                            <p className="text-[#0E1B2C]/80 leading-relaxed">{body}</p>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function WheelSection() {
    return (
        <section id="wheel" className="bg-white text-[#0E1B2C] py-24 px-6 lg:px-10" data-testid="wheel-section">
            <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <div>
                    <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#1E56A0] mb-3">The model</div>
                    <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#0B2545] font-semibold leading-tight">
                        The ESSS Organizational Coherence Model
                    </h2>
                    <p className="mt-6 text-[#0E1B2C]/80 leading-relaxed max-w-lg">
                        Six interdependent domains define whether a school system operates as a coherent whole. Click any segment to read its full definition.
                    </p>
                    <div className="mt-6 text-xs uppercase tracking-[0.25em] text-[#1E56A0] font-semibold">Click a segment ↓</div>
                </div>
                <div className="flex justify-center lg:justify-end">
                    <FrameworkWheel size={480} />
                </div>
            </div>
        </section>
    );
}

const STEP_CAPTIONS = {
    vision: "Set the direction: a shared, measurable vision the whole district can rally behind.",
    leadership: "Translate vision into disciplined execution across every leader.",
    culture: "Build the conditions — trust, safety, belonging — that make execution possible.",
    curriculum: "Align rigorous, standards-based content and assessment across every classroom.",
    instructional: "Elevate the teacher-student-content interaction where learning actually happens.",
    data: "Close the loop with monitoring that drives continuous improvement.",
};

function StepStrip() {
    const [active, setActive] = React.useState(null);
    return (
        <section id="framework" className="bg-[#D6E6F2] text-[#0E1B2C] py-24 px-6 lg:px-10" data-testid="framework-strip">
            <div className="max-w-7xl mx-auto">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
                    <div>
                        <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#1E56A0] mb-3">Six domains · One integrated system</div>
                        <h2 id="how" className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#0B2545] font-semibold leading-tight max-w-2xl">
                            How the frameworks work together
                        </h2>
                    </div>
                    <p className="text-[#0E1B2C]/75 max-w-md leading-relaxed">
                        Each domain feeds the next. Vision cascades into leadership; leadership shapes culture; culture enables curriculum; curriculum drives instructional core; data closes the loop.
                    </p>
                </div>

                <div className="overflow-x-auto pb-4 -mx-6 px-6 lg:mx-0 lg:px-0">
                    <div className="flex items-stretch gap-3 min-w-max lg:min-w-0 lg:grid lg:grid-cols-6">
                        {DOMAINS.map((d, i) => (
                            <div key={d.id} className="flex items-stretch gap-2 lg:contents">
                                <button
                                    type="button"
                                    onClick={() => setActive(active === d.id ? null : d.id)}
                                    onMouseEnter={() => setActive(d.id)}
                                    onMouseLeave={() => setActive((cur) => (cur === d.id ? null : cur))}
                                    className={`relative rounded-2xl p-5 min-w-[220px] lg:min-w-0 text-white overflow-hidden shadow-lg shadow-[#0B2545]/10 text-left transition-all duration-300 ${active === d.id ? "-translate-y-1" : ""}`}
                                    style={{ backgroundColor: d.color }}
                                    data-testid={`framework-step-${d.id}`}
                                >
                                    <div className="text-[10px] uppercase tracking-[0.2em] opacity-80 mb-2">Step {i + 1}</div>
                                    <div className="font-serif text-lg leading-tight font-semibold min-h-[52px]">{d.name}</div>
                                    <div className="mt-4 inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/15 backdrop-blur border border-white/25">
                                        <d.Icon size={16} />
                                    </div>
                                    <motion.div
                                        initial={false}
                                        animate={{ scaleX: active === d.id ? 1 : 0 }}
                                        className="absolute bottom-0 left-0 right-0 h-1 origin-left"
                                        style={{ backgroundColor: "#FFFFFF" }}
                                    />
                                </button>
                                {i < DOMAINS.length - 1 && (
                                    <div className="hidden lg:flex items-center justify-center text-[#1E56A0]">
                                        <ChevronRight size={20} />
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
                <AnimatePresence>
                    {active && (
                        <motion.div
                            initial={{ opacity: 0, y: -6 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -6 }}
                            className="mt-4 rounded-xl bg-white border border-[#8AC4FF] px-4 py-3 text-sm text-[#0B2545]"
                            data-testid={`framework-step-caption-${active}`}
                        >
                            <strong className="font-serif text-base">{DOMAINS.find((d) => d.id === active)?.name}</strong>
                            <span className="mx-2 text-[#0E1B2C]/40">—</span>
                            <span>{STEP_CAPTIONS[active]}</span>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </section>
    );
}

export default function HeroPage() {
    const scrollToHow = () => {
        const el = document.getElementById("how");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <div className="min-h-screen bg-[#0B2545] text-white font-sans" data-testid="hero-page">
            <PublicNav variant="light" />

            {/* Hero */}
            <section
                className="relative overflow-hidden grain"
                style={{ background: "linear-gradient(135deg, #0B2545 0%, #1E56A0 55%, #8AC4FF 120%)" }}
            >
                <motion.div
                    aria-hidden
                    className="absolute -top-24 -right-24 h-[560px] w-[560px] rounded-full bg-[#8AC4FF]/25 blur-3xl"
                    animate={{ y: [0, 24, 0] }}
                    transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
                />
                <motion.div
                    aria-hidden
                    className="absolute -bottom-40 -left-32 h-[520px] w-[520px] rounded-full bg-[#1E56A0]/40 blur-3xl"
                    animate={{ y: [0, -20, 0] }}
                    transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
                />

                <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-28 pb-16 lg:pt-36 lg:pb-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-7">
                        <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/15 backdrop-blur-md px-3 py-1 text-xs uppercase tracking-[0.2em] font-semibold text-white/85 mb-6"
                        >
                            <span className="h-1.5 w-1.5 rounded-full bg-[#8AC4FF]" />
                            Educational Strategic Systems & Solutions
                        </motion.div>
                        <MorphHeadline />
                        <motion.p
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.2 }}
                            className="mt-6 max-w-xl text-base sm:text-[1.15rem] text-white/85 leading-relaxed"
                            data-testid="hero-subhead"
                        >
                            Diagnose, align, execute, measure, and sustain educational coherence —
                            across every domain of your district.
                        </motion.p>
                        <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.35 }}
                            className="mt-8 flex flex-wrap items-center gap-4"
                        >
                            <Link to="/login" data-testid="hero-signin-btn">
                                <Button size="lg" className="rounded-full h-12 px-7 bg-white text-[#0B2545] hover:bg-[#D6E6F2] font-semibold shadow-lg shadow-[#0B2545]/30">
                                    Sign In
                                    <ArrowRight className="ml-1" size={18} />
                                </Button>
                            </Link>
                            <Button
                                size="lg"
                                variant="outline"
                                onClick={scrollToHow}
                                className="rounded-full h-12 px-7 bg-transparent border-white/40 text-white hover:bg-white/10"
                                data-testid="hero-learn-more-btn"
                            >
                                Learn More
                            </Button>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.6, delay: 0.55 }}
                            className="mt-8 flex flex-wrap items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-semibold text-white/70"
                            data-testid="hero-trust-strip"
                        >
                            {["6 Domains", "Audit-defensible", "Board-ready reports"].map((t) => (
                                <span key={t} className="inline-flex items-center gap-1.5 rounded-full bg-white/10 border border-white/15 px-3 py-1">
                                    <Sparkles size={11} /> {t}
                                </span>
                            ))}
                        </motion.div>
                    </div>

                    {/* Right column: teacher hero image with floating stat cards */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.96 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.8, ease: "easeOut", delay: 0.15 }}
                        className="lg:col-span-5 relative"
                    >
                        <div
                            aria-hidden
                            className="absolute inset-0 -m-8 rounded-[40px] blur-3xl"
                            style={{ background: "radial-gradient(circle at 50% 50%, rgba(138,196,255,0.55), transparent 65%)" }}
                        />
                        <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-black/40 border border-white/20" data-testid="hero-image-card">
                            <img
                                src="/hero-teacher.jpg"
                                alt="Educator leading a class"
                                className="w-full h-full object-cover object-center"
                                style={{ minHeight: 460, maxHeight: 620 }}
                            />
                            <div
                                aria-hidden
                                className="absolute inset-0"
                                style={{ background: "linear-gradient(180deg, transparent 40%, rgba(11,37,69,0.35) 100%)" }}
                            />
                        </div>

                        {/* Floating stat cards */}
                        <motion.div
                            animate={{ y: [0, -6, 0] }}
                            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                            className="absolute -top-3 -right-3 sm:top-6 sm:right-6 rounded-2xl bg-white text-[#0B2545] p-4 shadow-2xl shadow-[#0B2545]/40 border border-white flex items-center gap-3"
                            data-testid="hero-stat-ohi"
                        >
                            <OhiRing target={78} />
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#1E56A0]">OHI Score</div>
                                <div className="font-serif text-xl font-semibold leading-none">78/100</div>
                                <div className="text-[10px] text-[#0E1B2C]/60 mt-1">Riverside USD · Q1 2026</div>
                            </div>
                        </motion.div>

                        <motion.div
                            animate={{ y: [0, 5, 0] }}
                            transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut", delay: 0.6 }}
                            className="absolute -bottom-3 left-2 sm:bottom-6 sm:left-6 rounded-2xl bg-white text-[#0B2545] p-4 shadow-2xl shadow-[#0B2545]/40 border border-white"
                            data-testid="hero-stat-domains"
                        >
                            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#1E56A0]">6/6 Domains Scored</div>
                            <div className="mt-2 flex items-center gap-1.5">
                                {DOMAINS.map((d) => (
                                    <span key={d.id} className="h-3 w-3 rounded-full" style={{ backgroundColor: d.color }} />
                                ))}
                            </div>
                        </motion.div>

                        <motion.div
                            animate={{ y: [0, -4, 0] }}
                            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1.2 }}
                            className="absolute -bottom-3 right-2 sm:bottom-6 sm:right-6 rounded-2xl bg-[#0B2545] text-white p-4 shadow-2xl shadow-black/50 border border-white/20 flex items-center gap-3"
                            data-testid="hero-stat-prompts"
                        >
                            <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-white/10 border border-white/20">
                                <LineChart size={16} />
                            </span>
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#8AC4FF]">Reflection Prompts</div>
                                <div className="font-serif text-xl font-semibold leading-none">216</div>
                            </div>
                        </motion.div>
                    </motion.div>
                </div>
            </section>

            <StatsStrip />
            <DomainPreviewGrid />
            <SplitBanner />
            <ValueProps />
            <WheelSection />
            <StepStrip />

            {/* CTA band */}
            <section className="bg-[#0B2545] text-white py-20 px-6 lg:px-10" data-testid="cta-band">
                <div className="max-w-5xl mx-auto text-left lg:text-center">
                    <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight">
                        Ready to see coherence in your district?
                    </h2>
                    <p className="mt-4 text-white/80 max-w-2xl mx-auto">
                        Sign in with your admin-provisioned credentials. ESSS Coherence is invitation-only. Request access if your district has not been onboarded.
                    </p>
                    <div className="mt-8 flex flex-wrap gap-4 lg:justify-center">
                        <Link to="/login" data-testid="cta-signin-btn">
                            <Button size="lg" className="rounded-full h-12 px-7 bg-white text-[#0B2545] hover:bg-[#D6E6F2] font-semibold">
                                Sign In
                                <ArrowRight className="ml-1" size={18} />
                            </Button>
                        </Link>
                        <Link to="/contact" data-testid="cta-contact-btn">
                            <Button size="lg" variant="outline" className="rounded-full h-12 px-7 border-white/40 text-white bg-transparent hover:bg-white/10">
                                Contact Us
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>

            <PublicFooter />
        </div>
    );
}
